/**
 * BuyAgainRail — "Order again" horizontal rail surfaced for returning
 * customers. Powered by client-side aggregation over recent orders
 * (see `useBuyAgain`).
 *
 * Hydrates each product via `productsAPI.getProduct` so we have rich
 * card data (image, MRP, discount, stock).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionHeader, HorizontalRail, Skeleton } from '../../design';
import ProductCard from '../products/ProductCard';
import useBuyAgain from '../../hooks/useBuyAgain';
import useCartActions from '../../hooks/useCartActions';
import { productsAPI, Product } from '../../api/products';

const BuyAgainRail: React.FC = () => {
  const { hits, loading } = useBuyAgain(12);
  const cart = useCartActions();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [hydrating, setHydrating] = useState(false);

  useEffect(() => {
    if (hits.length === 0) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setHydrating(true);
      const rows = await Promise.all(
        hits.map((h) =>
          productsAPI.getProduct(h.product_id).catch(() => null),
        ),
      );
      if (cancelled) return;
      const usable = rows.filter((r): r is Product => !!r && r.is_available !== false);
      setProducts(usable);
      setHydrating(false);
    })();
    return () => { cancelled = true; };
  }, [hits]);

  if (!loading && hits.length === 0) return null;

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeader
        title="🔁 Order again"
        subtitle="Your usuals — one tap away"
        accent="primary"
        actionLabel={products.length > 4 ? 'See all' : undefined}
        onAction={() => navigate('/orders')}
      />
      {loading || hydrating ? (
        <HorizontalRail ariaLabel="Order again">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ width: 168 }}>
              <Skeleton.ProductCard />
            </div>
          ))}
        </HorizontalRail>
      ) : (
        <HorizontalRail ariaLabel="Order again" itemMinWidth={168}>
          {products.map((p) => (
            <ProductCard
              key={`${p.store_id}:${p.id}`}
              product={p}
              qty={cart.getQty(p)}
              adding={cart.isBusy(p)}
              onAdd={() => cart.add(p)}
              onIncrement={() => cart.increment(p)}
              onDecrement={() => cart.decrement(p)}
              variant="compact"
              badge={{ label: 'Reorder', variant: 'primary' }}
            />
          ))}
        </HorizontalRail>
      )}
    </section>
  );
};

export default BuyAgainRail;

